/**
 * Targeting for the home popup (`GET /v1/deeplink?placement=home_banner`). Mirrors the
 * backend's deeplink fields: a row with none of them is shown to everyone, exactly as
 * before targeting existed. Segment and cohort handling is shared with the Custom Ads page.
 */
import { cleanCountries } from '../custom-ads/slotTargeting';

export { SPENDER_SEGMENTS, segmentOptions } from '../custom-ads/slotTargeting';

export interface PopupTargetingFields {
  targetUserSegments?: string[] | null;
  maxImpressionsPerDay?: number | null;
  maxImpressionsTotalPerUser?: number | null;
  metadata?: {
    excludePayers?: boolean | null;
    /** undefined = default (the legendary card row hides after purchase, others do not). */
    hideAfterPurchase?: boolean | null;
    experiment?: { key?: string | null; variant?: string | null } | null;
    /** By player_locale country; no field on the form yet, a save keeps it (the server merges metadata). */
    countries?: { include?: string[] | null; exclude?: string[] | null } | null;
  } | null;
}

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** An experiment rule needs both its key and its variant; anything less counts as no rule. */
function experimentOf(
  experiment: { key?: string | null; variant?: string | null } | null | undefined
): { key: string; variant: string } | undefined {
  const key = experiment?.key;
  const variant = experiment?.variant;
  return typeof key === 'string' && key && typeof variant === 'string' && variant
    ? { key, variant }
    : undefined;
}

/**
 * Create: drops empty targeting so an untargeted row saves exactly as before.
 * Edit: sends null for every cleared field so the server clears it (metadata keys are
 * merged server-side, so other metadata keys are kept).
 */
export function cleanPopupTargeting<T extends PopupTargetingFields>(
  values: T,
  editing: boolean
): T {
  const {
    targetUserSegments,
    maxImpressionsPerDay,
    maxImpressionsTotalPerUser,
    metadata,
    ...rest
  } = values as Record<string, unknown> & PopupTargetingFields;
  const out: Record<string, unknown> = { ...rest };

  const segments = Array.isArray(targetUserSegments) && targetUserSegments.length > 0;
  if (segments) out.targetUserSegments = targetUserSegments;
  else if (editing) out.targetUserSegments = null;

  for (const [name, value] of [
    ['maxImpressionsPerDay', maxImpressionsPerDay],
    ['maxImpressionsTotalPerUser', maxImpressionsTotalPerUser],
  ] as const) {
    if (isNumber(value)) out[name] = value;
    else if (editing) out[name] = null;
  }

  const { excludePayers, hideAfterPurchase, experiment, ...otherMetadata } = (metadata ??
    {}) as Record<string, unknown> & NonNullable<PopupTargetingFields['metadata']>;
  const nextMetadata: Record<string, unknown> = { ...otherMetadata };
  if (excludePayers === true) nextMetadata.excludePayers = true;
  else if (editing) nextMetadata.excludePayers = null;
  if (typeof hideAfterPurchase === 'boolean') nextMetadata.hideAfterPurchase = hideAfterPurchase;
  else if (editing) nextMetadata.hideAfterPurchase = null;
  const rule = experimentOf(experiment);
  if (rule) nextMetadata.experiment = rule;
  else if (editing) nextMetadata.experiment = null;
  if (Object.keys(nextMetadata).length > 0) out.metadata = nextMetadata;

  return out as T;
}

/** One line for the table, e.g. "non-payer · not payers · 1/day · 5 total · hide after purchase · popup_v1=on". */
export function describePopupTargeting(row: PopupTargetingFields): string {
  const parts: string[] = [];
  if (row.targetUserSegments && row.targetUserSegments.length > 0) {
    parts.push(row.targetUserSegments.join(', '));
  }
  if (row.metadata?.excludePayers) parts.push('not payers');
  if (isNumber(row.maxImpressionsPerDay)) parts.push(`${row.maxImpressionsPerDay}/day`);
  if (isNumber(row.maxImpressionsTotalPerUser)) {
    parts.push(`${row.maxImpressionsTotalPerUser} total`);
  }
  if (row.metadata?.hideAfterPurchase === true) parts.push('hide after purchase');
  if (row.metadata?.hideAfterPurchase === false) parts.push('keep after purchase');
  const rule = experimentOf(row.metadata?.experiment);
  if (rule) parts.push(`${rule.key}=${rule.variant}`);
  const countries = cleanCountries(row.metadata?.countries);
  if (countries?.include) parts.push(`${countries.include.join(', ')} only`);
  if (countries?.exclude) parts.push(`not ${countries.exclude.join(', ')}`);
  return parts.length > 0 ? parts.join(' · ') : 'everyone';
}
